import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Company } from '@/lib/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { UserManagement } from '@/components/admin/user-management';
import { CompanyManagement } from '@/components/admin/CompanyManagement';
import { AccessRequestsPanel } from '@/components/admin/AccessRequestsPanel';
import { RoleAuditLog } from '@/components/admin/RoleAuditLog';
import { Loader2 } from 'lucide-react';
import AdminSettings from '@/components/admin/AdminSettings';
import { SubscriptionPlansManagement } from '@/components/admin/subscription-plans/SubscriptionPlansManagement';
import { SystemEmailSettings } from '@/components/admin/SystemEmailSettings';
import { EmailMonitoring } from '@/components/admin/EmailMonitoring';
import { LeadAnalyticsDashboard } from '@/components/admin/LeadAnalyticsDashboard';

export default function Admin() {
  const { profile, isLoading } = useAuth();
  const { toast } = useToast();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState('access-requests');

  useEffect(() => {
    if (profile?.role === 'admin') {
      fetchCompanies();
    }
  }, [profile]);

  const fetchCompanies = async () => {
    try {
      setIsLoadingData(true);
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;

      setCompanies(data || []);
    } catch (error: any) {
      toast({
        title: "Failed to load companies",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoadingData(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 container mx-auto p-4 md:p-6 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto" />
          <p className="mt-2 text-muted-foreground">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (profile?.role !== 'admin') {
    return (
      <div className="flex-1 container mx-auto p-4 md:p-6">
        <Card className="text-center p-10">
          <CardContent className="pt-10">
            <h3 className="mt-4 text-lg font-medium">Access Denied</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              You don't have permission to access the admin panel.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 space-y-4 sm:space-y-6 md:space-y-8">
      <div>
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Admin Control Panel</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1">Manage users, companies, and system settings</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="overflow-x-auto -mx-3 sm:mx-0 mb-4 sm:mb-6 pb-1">
          <TabsList className="inline-flex w-max h-auto p-1 bg-muted">
            <TabsTrigger value="access-requests" className="whitespace-nowrap px-3 py-2 text-sm">
              Access Requests
            </TabsTrigger>
            <TabsTrigger value="users" className="whitespace-nowrap px-3 py-2 text-sm">
              Users
            </TabsTrigger>
            <TabsTrigger value="role-audit" className="whitespace-nowrap px-3 py-2 text-sm">
              Role Audit
            </TabsTrigger>
            <TabsTrigger value="companies" className="whitespace-nowrap px-3 py-2 text-sm">
              Companies
            </TabsTrigger>
            <TabsTrigger value="system-email" className="whitespace-nowrap px-3 py-2 text-sm">
              System Email
            </TabsTrigger>
            <TabsTrigger value="monitoring" className="whitespace-nowrap px-3 py-2 text-sm">
              Monitoring
            </TabsTrigger>
            <TabsTrigger value="settings" className="whitespace-nowrap px-3 py-2 text-sm">
              Settings
            </TabsTrigger>
            <TabsTrigger value="subscription-plans" className="whitespace-nowrap px-3 py-2 text-sm">
              Plans
            </TabsTrigger>
            <TabsTrigger value="lead-analytics" className="whitespace-nowrap px-3 py-2 text-sm">
              Lead Analytics
            </TabsTrigger>
          </TabsList>
        </div>
        
        <TabsContent value="access-requests" className="mt-0">
          <Card>
            <CardContent className="p-3 sm:p-4 md:p-6">
              <AccessRequestsPanel />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="users" className="mt-0">
          <Card>
            <CardContent className="p-3 sm:p-4 md:p-6">
              <UserManagement companies={companies} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="role-audit" className="mt-0">
          <Card>
            <CardContent className="p-3 sm:p-4 md:p-6">
              <RoleAuditLog />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="companies" className="mt-0">
          <Card>
            <CardContent className="p-3 sm:p-4 md:p-6">
              <CompanyManagement 
                companies={companies} 
                isLoading={isLoadingData}
                onRefresh={fetchCompanies}
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="system-email" className="mt-0">
          <Card>
            <CardContent className="p-3 sm:p-4 md:p-6">
              <SystemEmailSettings />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="monitoring" className="mt-0">
          <Card>
            <CardContent className="p-3 sm:p-4 md:p-6">
              <EmailMonitoring />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="settings" className="mt-0">
          <Card>
            <CardContent className="p-3 sm:p-4 md:p-6">
              <AdminSettings />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="subscription-plans" className="mt-0">
          <Card>
            <CardContent className="p-3 sm:p-4 md:p-6">
              <SubscriptionPlansManagement />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="lead-analytics" className="mt-0">
          <Card>
            <CardContent className="p-3 sm:p-4 md:p-6">
              <LeadAnalyticsDashboard />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
